// API base URL - can be configured via environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Auth types
export interface User {
  id: string;
  email: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  emailVerified: boolean;
  isOnboarded: boolean;
  isAdmin: boolean;
  preferences: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  success: true;
  message: string;
  data: {
    user: User;
    token: string;
    sessionId: string;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

// Admin API response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Admin dashboard types
export interface Comment {
  id: string;
  content: string;
  rating: number;
  userId: string;
  videoId: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  user: {
    username: string;
    avatarUrl: string | null;
  };
  video: {
    id: string;
    title: string;
    slug: string;
  };
}

export interface Report {
  id: string;
  type: 'comment' | 'video' | 'user';
  reason: string;
  description: string | null;
  status: 'pending' | 'resolved' | 'dismissed';
  reporterId: string;
  targetId: string;
  createdAt: string;
  updatedAt: string;
  reporter: {
    username: string;
  };
}

export interface UserListItem {
  id: string;
  email: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
  isActive: boolean;
  isAdmin: boolean;
  createdAt: string;
  _count: {
    ratings: number;
    comments: number;
    savedVideos: number;
    favoriteCasts: number;
  };
}

export interface FeaturedContent {
  id: string;
  type: 'idol' | 'studio' | 'category';
  targetId: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AnalyticsData {
  totalUsers: number;
  totalVideos: number;
  totalComments: number;
  totalReports: number;
  pendingReports: number;
  recentUsers: number; // last 30 days
  recentComments: number; // last 30 days
  activeNow: number; // last 5 minutes
  activeToday: number; // today
  inactiveUsers: number; // 30+ days inactive
}

// Content Management types
export interface Video {
  id: string;
  contentId: string;
  dvdId: string | null;
  title: string;
  jpTitle: string | null;
  releaseDate: string;
  duration: number | null;
  studio: {
    name: string;
    slug: string;
  } | null;
  _count: {
    casts: number;
    categories: number;
  };
}

export interface VideoFull {
  id: string;
  contentId: string;
  dvdId: string | null;
  title: string;
  jpTitle: string | null;
  releaseDate: string | null;
  duration: number | null;
  coverImage: string | null;
  trailerUrl: string | null;
  studio: {
    id: string;
    slug: string;
    name: string;
    jpName: string | null;
  } | null;
  studioId: string | null;
  series: {
    id: string;
    slug: string;
    name: string;
    jpName: string | null;
  } | null;
  seriesId: string | null;
  casts: {
    id: string;
    slug: string;
    name: string;
    jpName: string | null;
    avatar: string | null;
  }[];
  genres: {
    id: string;
    slug: string;
    name: string;
    jpName: string | null;
    zhName: string | null;
  }[];
}

export interface Idol {
  id: string;
  slug: string;
  name: string;
  jpName: string | null;
  avatar: string | null;
  _count: {
    videos: number;
  };
}

export interface Category {
  id: string;
  slug: string;
  name: string;
  jpName: string | null;
  zhName: string | null;
  _count: {
    videos: number;
  };
}

export interface Studio {
  id: string;
  slug: string;
  name: string;
  jpName: string | null;
  _count: {
    videos: number;
  };
}

export interface Series {
  id: string;
  slug: string;
  name: string;
  jpName: string | null;
  _count: {
    videos: number;
  };
}

// API Client
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
      throw errorData;
    }

    return response.json();
  }

  // Auth APIs
  async login(data: LoginRequest): Promise<AuthResponse> {
    return this.fetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async logout(): Promise<{ success: true; message: string }> {
    return this.fetch('/auth/logout', {
      method: 'POST',
    });
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.fetch('/user/profile');
  }

  // Admin APIs (to be implemented)
  async getComments(params: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<PaginatedResponse<Comment>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.status) query.set('status', params.status);
    return this.fetch(`/admin/comments?${query.toString()}`);
  }

  async updateCommentStatus(commentId: string, status: 'approved' | 'rejected'): Promise<ApiResponse<Comment>> {
    return this.fetch(`/admin/comments/${commentId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async getReports(params: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
  }): Promise<ApiResponse<PaginatedResponse<Report>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.status) query.set('status', params.status);
    if (params.type) query.set('type', params.type);
    return this.fetch(`/admin/reports?${query.toString()}`);
  }

  async updateReportStatus(reportId: string, status: 'resolved' | 'dismissed'): Promise<ApiResponse<Report>> {
    return this.fetch(`/admin/reports/${reportId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async getVideoById(
    videoId: string
  ): Promise<ApiResponse<{ id: string; contentId: string; title: string; dvdId: string | null }>> {
    return this.fetch(`/admin/videos/${videoId}`);
  }

  async getUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<UserListItem>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    return this.fetch(`/admin/users?${query.toString()}`);
  }

  async updateUserStatus(
    userId: string,
    data: { isActive?: boolean; isAdmin?: boolean }
  ): Promise<ApiResponse<UserListItem>> {
    return this.fetch(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getAnalytics(): Promise<ApiResponse<AnalyticsData>> {
    return this.fetch('/admin/analytics');
  }

  async getFeaturedContent(): Promise<ApiResponse<FeaturedContent[]>> {
    return this.fetch('/admin/featured');
  }

  async updateFeaturedContent(data: {
    featuredCasts?: string[];
    featuredStudios?: string[];
    featuredCategories?: string[];
    featuredVideos?: string[];
  }): Promise<ApiResponse<any>> {
    return this.fetch('/admin/featured', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async clearFeaturedCache(): Promise<ApiResponse<{ message: string }>> {
    return this.fetch('/admin/featured/cache', {
      method: 'DELETE',
    });
  }

  async getSettings(): Promise<ApiResponse<{ commentDefaultStatus: string }>> {
    return this.fetch('/admin/settings');
  }

  async updateSettings(data: { commentDefaultStatus: string }): Promise<ApiResponse<any>> {
    return this.fetch('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Content Management APIs
  async getVideos(params: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<Video>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    return this.fetch(`/admin/videos?${query.toString()}`);
  }

  async getIdols(params: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<Idol>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    return this.fetch(`/admin/idols?${query.toString()}`);
  }

  async getCategories(params: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<Category>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    return this.fetch(`/admin/categories?${query.toString()}`);
  }

  async getStudios(params: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<Studio>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    return this.fetch(`/admin/studios?${query.toString()}`);
  }

  async getSeries(params: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<Series>>> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.search) query.set('search', params.search);
    return this.fetch(`/admin/series?${query.toString()}`);
  }

  async updateSeries(
    seriesId: string,
    data: { slug: string; name: string; jpName?: string }
  ): Promise<ApiResponse<Series>> {
    return this.fetch(`/admin/series/${seriesId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteSeries(seriesId: string): Promise<ApiResponse<{ message: string }>> {
    return this.fetch(`/admin/series/${seriesId}`, {
      method: 'DELETE',
    });
  }

  async updateStudio(
    studioId: string,
    data: { slug: string; name: string; jpName?: string }
  ): Promise<ApiResponse<Studio>> {
    return this.fetch(`/admin/studios/${studioId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteStudio(studioId: string): Promise<ApiResponse<{ message: string }>> {
    return this.fetch(`/admin/studios/${studioId}`, {
      method: 'DELETE',
    });
  }

  async updateCategory(
    categoryId: string,
    data: { slug: string; name: string; jpName?: string; zhName?: string }
  ): Promise<ApiResponse<Category>> {
    return this.fetch(`/admin/categories/${categoryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(categoryId: string): Promise<ApiResponse<{ message: string }>> {
    return this.fetch(`/admin/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  async updateIdol(
    idolId: string,
    data: { slug: string; name: string; jpName?: string; avatar?: string }
  ): Promise<ApiResponse<Idol>> {
    return this.fetch(`/admin/idols/${idolId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteIdol(idolId: string): Promise<ApiResponse<{ message: string }>> {
    return this.fetch(`/admin/idols/${idolId}`, {
      method: 'DELETE',
    });
  }

  // Search APIs for dropdowns
  async searchStudios(query: string): Promise<ApiResponse<Studio[]>> {
    return this.fetch(`/admin/studios/search?q=${encodeURIComponent(query)}`);
  }

  async searchSeries(query: string): Promise<ApiResponse<Series[]>> {
    return this.fetch(`/admin/series/search?q=${encodeURIComponent(query)}`);
  }

  async searchIdols(query: string): Promise<ApiResponse<Idol[]>> {
    return this.fetch(`/admin/idols/search?q=${encodeURIComponent(query)}`);
  }

  async searchCategories(query: string): Promise<ApiResponse<Category[]>> {
    return this.fetch(`/admin/categories/search?q=${encodeURIComponent(query)}`);
  }

  // Video full data (for editing)
  async getVideoFull(videoId: string): Promise<ApiResponse<VideoFull>> {
    return this.fetch(`/admin/videos/${videoId}/full`);
  }

  async updateVideo(
    videoId: string,
    data: {
      dvdId?: string;
      contentId?: string;
      title?: string;
      jpTitle?: string;
      coverImage?: string;
      trailerUrl?: string;
      duration?: number;
      releaseDate?: string;
      studioId?: string | null;
      seriesId?: string | null;
      castIds?: string[];
      genreIds?: string[];
    }
  ): Promise<ApiResponse<VideoFull>> {
    return this.fetch(`/admin/videos/${videoId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteVideo(videoId: string): Promise<ApiResponse<{ message: string }>> {
    return this.fetch(`/admin/videos/${videoId}`, {
      method: 'DELETE',
    });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
